import { Component, DestroyRef, inject, OnInit, signal, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TooltipModule } from 'primeng/tooltip';
import { HugeiconsIconComponent } from '@hugeicons/angular';
import {
  Chart02Icon,
  Briefcase01Icon,
  Building03Icon,
  Cancel01Icon,
  ChevronDownIcon,
  ChevronRightIcon,
  Coins01Icon,
  ComputerIcon,
  DashboardSquare01Icon,
  HistoryIcon,
  InformationCircleIcon,
  LockKeyholeIcon,
  Location01Icon,
  Menu01Icon,
  SecurityCheckIcon,
  Settings01Icon,
  Shield01Icon,
  SlidersHorizontalIcon,
  UserGroupIcon,
  UserMultipleIcon,
} from '@hugeicons/core-free-icons';
import { SidebarStateService } from '@core/services/sidebar-state.service';
import { AuthService } from '@auth/services/auth.service';

import { SideBarEstatisticas } from '@shared/api/services/estatisticas/estatisticas-info.service';
import { SideBarEstatisticasModel } from '@shared/api/services/estatisticas/estatisticas-models';
import { FaturacaoEletronicaService } from '@shared/api/services/faturacao-eletronica.service';

@Component({
  selector: 'app-side-bar',
  standalone: true,
  imports: [RouterModule, CommonModule, TooltipModule, HugeiconsIconComponent],
  templateUrl: './side-bar.component.html',
  styleUrls: ['./side-bar.component.scss'], 
})
export class SideBarComponent implements OnInit {
  private readonly sidebarService = inject(SideBarEstatisticas);
  private readonly faturacaoService = inject(FaturacaoEletronicaService);
  public readonly state = inject(SidebarStateService);
  private readonly auth = inject(AuthService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  expandedMenus = signal<Set<string>>(
    new Set([
      'operacao',
      'monitorizacao',
      'configuracoes',
      'controlo-acesso',
    ]),
  );

  badges: SideBarEstatisticasModel | null = null;

  readonly dashboardIcon = DashboardSquare01Icon;
  readonly briefcaseIcon = Briefcase01Icon;
  readonly userGroupIcon = UserGroupIcon;
  readonly userMultipleIcon = UserMultipleIcon;
  readonly locationIcon = Location01Icon;
  readonly shieldIcon = Shield01Icon;
  readonly securityCheckIcon = SecurityCheckIcon;
  readonly coinsIcon = Coins01Icon;
  readonly informationCircleIcon = InformationCircleIcon;
  readonly buildingIcon = Building03Icon;
  readonly computerIcon = ComputerIcon;
  readonly chart02Icon = Chart02Icon;
  readonly historyIcon = HistoryIcon;
  readonly settingsIcon = Settings01Icon;
  readonly lockKeyholeIcon = LockKeyholeIcon;
  readonly slidersIcon = SlidersHorizontalIcon;
  readonly menuIcon = Menu01Icon;
  readonly cancelIcon = Cancel01Icon;
  readonly chevronDownIcon = ChevronDownIcon;
  readonly chevronRightIcon = ChevronRightIcon;

  // Getters para extrair o campo `valor` de cada BadgeItem
  get totalColaboradores(): number {
    return this.badges?.efetivos?.valor ?? 0;
  }

  get totalPostos(): number {
    return this.badges?.postos?.valor ?? 0;
  }

  get totalEquipamentos(): number {
    return this.badges?.equipamentos?.valor ?? 0;
  }

  get totalOcorrencias(): number {
    return this.badges?.ocorrencias?.valor ?? 0;
  }

  get totalFaturas(): number {
    if (this.badges?.faturas?.valor !== undefined) {
      return this.badges.faturas.valor;
    }
    if (this.badges?.financeiro?.valor !== undefined) {
      return this.badges.financeiro.valor;
    }
    return this.faturacaoService.totalDocumentos();
  }

  get totalClientes(): number {
    return this.badges?.clientes?.valor ?? 0;
  }

  get userName(): string {
    return this.auth.getUserName() || 'Utilizador';
  }

  get userRole(): string {
    const grupos = this.auth.getGrupos();
    return (grupos && grupos.length > 0) ? grupos[0] : 'Administrador';
  }

  get userInitials(): string {
    const name = this.userName.trim();
    if (!name) return 'U';
    const parts = name.split(' ').filter(p => p.length > 0);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  toggleMenu(menu: string): void {
    if (this.state.collapsed()) {
      this.state.toggleCollapse();
      const next = new Set(this.expandedMenus());
      next.add(menu);
      this.expandedMenus.set(next);
      return;
    }

    const next = new Set(this.expandedMenus());
    if (next.has(menu)) {
      next.delete(menu);
    } else {
      next.add(menu);
    }
    this.expandedMenus.set(next);
  }

  isExpanded(menu: string): boolean {
    return this.expandedMenus().has(menu);
  }

  toggleCollapse(): void {
    this.state.toggleCollapse();
  }

  closeMobile(): void {
    this.state.closeMobile();
  }

  ngOnInit(): void {
    this.sidebarService.refreshRequested$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.carregarBadges());
    this.carregarBadges();
    this.carregarTotalFaturas();
  }

  private carregarTotalFaturas(): void {
    this.faturacaoService.obterTotalDocumentos().subscribe({
      next: () => {
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.warn('Erro ao carregar contagem de faturas para o sidebar:', err);
      },
    });
  }

  private carregarBadges(): void {
    this.sidebarService.getBadges().subscribe({
      next: (res) => {
        this.badges = res;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Erro ao carregar badges do menu:', err);
      },
    });
  }
}
